import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, isEmpty, noop } from 'lodash-es';
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { useTimeout } from 'site-modules/shared/hooks/use-timeout';
import {
  IncentivesWizardEntities,
  IncentivesWizardModel,
  IncentivesWizardPaths,
} from 'client/data/models/incentives-wizard';
import { STEP_FACTORY_COMPONENTS_MAP } from 'site-modules/shared/components/incentives/incentives-wizard/factories/step-factory-components-map';
import { SOURCES } from 'client/utils/image-helpers';
import { getCreativeId } from 'site-modules/shared/components/incentives/incentives-wizard/utils/get-creative-id';
import { maybe, wrapInArray } from 'site-modules/shared/utils/incentives/gql-incentives-filter';
import { SrpTopIncentivesText } from 'site-modules/shared/components/inventory/srp-top-incentives-text/srp-top-incentives-text';
import { Image } from 'site-modules/shared/components/image/image';
import { PersistedStateWizardContentAsync } from 'site-modules/shared/components/inventory/incentives-wizard/wizard-content/persisted-state-wizard-content-async';
import { IncentivesWizardDrawer } from 'site-modules/shared/components/inventory/incentives-wizard/incentives-wizard-drawer/incentives-wizard-drawer';
import { SrpTopIncentivesButton } from 'site-modules/shared/components/inventory/incentives-wizard/srp-top-incentives/srp-top-incentives-button';
import { SrpTopIncentivesContextProvider } from 'site-modules/shared/components/inventory/incentives-wizard/srp-top-incentives/srp-top-incentives-context-provider';

import './srp-top-incentives.scss';

function SrpTopIncentivesUI({
  incentives,
  isMobile = false,
  engineTypes,
  attributes = {},
  setModelValue,
  defaultYear = 0,
  bodyType = '',
  pagePublicationState,
  wizardState,
  onFinish,
  onStartOver = noop,
  isCrawler = false,
  isNational = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [setDrawerTimeout] = useTimeout();
  const onToggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const makeSlug = get(attributes, 'vehicle.make.niceId', '');
  const modelSlug = get(attributes, 'vehicle.model.niceId', '');
  const inventoryCode = get(attributes, 'inventoryType');
  const make = get(attributes, 'vehicle.make.name', '');
  const model = get(attributes, 'vehicle.model.name', '');
  const year = get(attributes, 'vehicle.modelYear.year');

  const creativeId = getCreativeId({ inventoryType: pagePublicationState, incentives });

  const handleButtonClick = useCallback(() => {
    setDrawerTimeout(async () => {
      await setModelValue(IncentivesWizardPaths.getVehicle(), IncentivesWizardModel, {
        makeSlug,
        modelSlug,
        inventoryCode,
        engineTypes,
        year: year || defaultYear,
        years: maybe(year).map(wrapInArray).unpack(),
      });

      await setModelValue(IncentivesWizardPaths.getIncentivesWizardData(), IncentivesWizardModel, incentives);
      setIsOpen(prev => !prev);
    }, 0);
  }, [defaultYear, engineTypes, incentives, inventoryCode, makeSlug, modelSlug, setDrawerTimeout, setModelValue, year]);

  return (
    <div
      className={classnames(
        'srp-top-incentives d-flex justify-content-between align-items-center pos-r overflow-hidden mb-1',
        { 'flex-column': isMobile }
      )}
      style={{ borderRadius: '1rem', padding: '1.5rem' }}
      data-tracking-parent={creativeId}
    >
      <Image
        className={classnames('pos-a top-0', {
          'h-100 right-0': isMobile,
          'img-desktop': !isMobile,
        })}
        style={{ ...(!isMobile ? { right: '130px' } : {}) }}
        imageId={`/incentives-wizard/wizard-flash-icon.svg`}
        source={SOURCES.STATIC}
        lazy
      />

      <SrpTopIncentivesContextProvider incentives={incentives} wizardState={wizardState}>
        <SrpTopIncentivesText
          className="me-1 z-index-1"
          make={make}
          model={model}
          isCrawler={isCrawler}
          isNational={isNational}
        />
        <SrpTopIncentivesButton isMobile={isMobile} onClick={handleButtonClick} />
      </SrpTopIncentivesContextProvider>

      <IncentivesWizardDrawer
        onToggle={onToggle}
        isOpen={isOpen}
        isMobile={isMobile}
        make={make}
        model={model}
        year={year}
        bodyType={bodyType}
        type={pagePublicationState}
        engineTypes={engineTypes}
        creativeId={creativeId}
        initialStepIndex={isEmpty(wizardState) ? 0 : -1}
        initialEligibleIncentivesIds={wizardState?.eligibleIncentiveIds}
        savedAnswers={wizardState?.answers}
        stepFactoryComponentsMap={STEP_FACTORY_COMPONENTS_MAP}
        component={PersistedStateWizardContentAsync}
        onRebatesSaveSum={onFinish}
        onStartOver={onStartOver}
      />
    </div>
  );
}

SrpTopIncentivesUI.propTypes = {
  incentives: IncentivesWizardEntities.IncentivesData,
  isMobile: PropTypes.bool,
  engineTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  defaultYear: PropTypes.number,
  bodyType: PropTypes.string,
  attributes: PropTypes.shape({
    vehicle: PropTypes.shape({
      make: PropTypes.shape({
        name: PropTypes.string,
        niceId: PropTypes.string,
      }),
      model: PropTypes.shape({
        name: PropTypes.string,
        niceId: PropTypes.string,
      }),
      modelYear: PropTypes.shape({ year: PropTypes.number }),
    }),
    inventoryType: PropTypes.string,
  }),
  setModelValue: PropTypes.func.isRequired,
  onFinish: PropTypes.func.isRequired,
  pagePublicationState: PropTypes.string.isRequired,
  wizardState: IncentivesWizardEntities.IncentivesWizardUnwrappedPersistedState,
  onStartOver: PropTypes.func,
  isCrawler: PropTypes.bool,
  isNational: PropTypes.bool,
};

export const SrpTopIncentives = connectToModel(SrpTopIncentivesUI, {});
